import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellOff, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert URL-safe base64 (VAPID) → Uint8Array required by pushManager.subscribe.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Host-facing toggle: subscribe/unsubscribe from EasyStay browser push.
 *  When a guest confirms check-in, the backend fires a push notification
 *  in addition to the existing email + SMS. Requires HTTPS + a permission
 *  grant (called after the user clicks the toggle to satisfy browser
 *  gesture requirements). */
export default function PushNotificationsToggle() {
  const [supported, setSupported] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);

  const check = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setSupported(false);
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setEnabled(!!sub);
    } catch { /* ignore */ }
    try {
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      setVapidKey(data.public_key || null);
    } catch { /* server may not have keys configured */ }
  }, []);

  useEffect(() => { check(); }, [check]);

  const enable = async () => {
    if (!vapidKey) return toast.error("Push notifications aren't configured on the server yet.");
    try {
      setBusy(true);
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications permission denied. Enable it in your browser settings and try again.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      // pushManager returns a PushSubscription with endpoint + keys; we send it verbatim
      // (JSON.stringify on the object serialises correctly).
      const json = JSON.parse(JSON.stringify(sub));
      await axios.post(`${API}/push/subscribe`, {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || "",
      });
      setEnabled(true);
      toast.success("Check-in nudges enabled — we'll ping this device the moment a guest checks in.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not enable push");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    try {
      setBusy(true);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }); } catch { /* still remove locally */ }
        await sub.unsubscribe();
      }
      setEnabled(false);
      toast.success("Check-in nudges disabled for this device.");
    } catch (e) {
      toast.error(e.message || "Could not disable push");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500" data-testid="push-unsupported">
        <BellOff className="mr-1 inline h-4 w-4" />
        Browser push isn&apos;t supported on this device. You&apos;ll still get email + SMS on check-in.
      </div>
    );
  }

  return (
    <div
      data-testid="push-toggle"
      className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 ${
        enabled ? "border-forest/20 bg-forest/5" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 font-semibold text-slate-900">
          <Bell className={`h-4 w-4 ${enabled ? "text-forest" : "text-copper"}`} />
          {enabled ? "Check-in nudges on" : "Get an instant nudge when a guest checks in"}
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          {enabled
            ? "This device will show a browser notification the moment any of your guests confirms check-in."
            : "Enable browser notifications so you can act on new check-ins without opening your email."}
        </div>
      </div>
      {enabled ? (
        <button
          onClick={disable}
          disabled={busy}
          data-testid="push-disable-btn"
          className="btn-outline text-xs !py-2 !px-3 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellOff className="mr-1 inline h-3 w-3" />}
          Turn off
        </button>
      ) : (
        <button
          onClick={enable}
          disabled={busy}
          data-testid="push-enable-btn"
          className="btn-primary flex items-center gap-1 text-xs !py-2 !px-3 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bell className="h-3 w-3" />}
          Enable nudges
        </button>
      )}
    </div>
  );
}
